import { defineComponent as _defineComponent } from 'vue'
import { sanitize } from 'dompurify';
import { computed, ref, watch } from 'vue';
import { Route } from 'vue-router';

import { CollectionType } from '@/enums/collection-type';
import { Viewport } from '@/enums/viewport';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { getViewport } from '@/helpers/ui-helper';
import { useRoutingStore, useSearchSuggesterStore } from '@/helpers/composables/useStores';
import { useI18n } from '@/helpers/composables/useI18n';
import { useMounted } from '@vueuse/core';


export default /*#__PURE__*/_defineComponent({
  __name: 'SearchSuggesterBar',
  props: {
    collectionType: null
  },
  emits: ["ionCancel"],
  setup(__props: any, { emit: emits }: { emit: ({
	(e: 'ionCancel'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
	collectionType: CollectionType;
};

const SearchSuggesterResults = () =>
	import(
		/* webpackChunkName: "search-suggester-results" */ '@/components/search/SearchSuggesterResults/SearchSuggesterResults.vue'
	);




const debounceValue = 300;
const isMounted = useMounted();
const inputQuery = ref('');
const searchbar = ref<HTMLIonSearchbarElement | null>(null);

const searchResults = ref<{ $children?: any[] } | null>(null);
const index = ref(-1);

const { t } = useI18n();
const { activeRoute } = useRoutingStore();
const { isSearchSuggesterOpen, query, search, goToSearch, setSearchSuggesterVisibility } = useSearchSuggesterStore();

const viewport = computed<string>(() => getViewport());

const placeholder = computed(() =>
	viewport.value === Viewport.DESKTOP ? t('WEBAPP_SEARCH_PLACEHOLDER') : t('WEBAPP_SEARCH')
);

const showCancelButtonMobileWebApp = computed(
	() => viewport.value === Viewport.SMARTPHONE && inputQuery.value?.length > 0 && isSearchSuggesterOpen.value
);

const showCancelButton = computed(() => {
	const forMobile = props.collectionType === CollectionType.SEARCH ? 'never' : 'always';
	return showCancelButtonMobileWebApp.value ? forMobile : 'never';
});

watch(
	() => props.collectionType,
	() => {
		if (props.collectionType !== CollectionType.SEARCH) {
			emits('ionCancel');
		}
	}
);

watch(query, () => {
	if (!isSearchSuggesterOpen.value && !inputQuery.value) {
		inputQuery.value = query.value;
	}
});

watch(activeRoute, (route: Route) => {
	closeSearchBar(false, true);

	// if the route has changed to a persons page, replace the input with the proper name of the person
	if (route.query.person_id && searchbar.value) {
		(searchbar.value as HTMLIonSearchbarElement).value = route.query.q as string;
	}
});
/**
 * close results when the route has been changed.
 */

async function openSearchBar(evt: any) {
	// INP case: artificially give the JS thread time to breathe by adding a re-render.
	await new Promise(resolve => setTimeout(resolve, 0));

	if (!isSearchSuggesterOpen.value) {
		const userInput = sanitize(evt.target.value);

		search({ searchQuery: userInput });
		setSearchSuggesterVisibility(true);

		// TITLE_DETAIL_REDESIGN_NEW_BUYBOX
		// trackTitleDetailRedesignNewBuyBoxExp({ action: 'click', label: 'open_suggester' });
		// TITLE_DETAIL_REDESIGN_NEW_BUYBOX
	}
}

function closeSearchBar(click: boolean = false, forceClose: boolean = false) {
	// if user is on search, we want to keep the search bar
	//if user navigates to title page from search, close
	if (props.collectionType !== CollectionType.SEARCH || forceClose) {
		emits('ionCancel');
		setSearchSuggesterVisibility(false);
		if (click) {
			// TITLE_DETAIL_REDESIGN_NEW_BUYBOX
			// trackTitleDetailRedesignNewBuyBoxExp({ action: 'click', label: 'close_suggester' });
			// TITLE_DETAIL_REDESIGN_NEW_BUYBOX
		}
	}
}

function onChange(evt: any) {
	index.value = -1;

	const userInput = sanitize(evt.target.value);
	inputQuery.value = userInput;
	search({ searchQuery: inputQuery.value });
}

async function editSearch(searchTerm?: string) {
	if (searchbar.value == null) return;

	await searchbar.value.setFocus();
	searchbar.value.value = searchTerm;
}

function navigateElements(evt: KeyboardEvent) {
	const { code, target } = evt;
	const userInput = sanitize((target as HTMLInputElement).value);
	switch (code) {
		case 'Escape':
			clear('escape');
			if (!userInput.length) {
				closeSearchBar();
			}
			break;
		case 'ArrowDown':
			evt.preventDefault();
			move('down');
			break;
		case 'Enter':
			if (userInput.length >= 2) {
				navigateToSearchPage(userInput);
			}

			break;
		case 'Tab':
		case 'Backspace':
		case 'ControlLeft':
		case 'ControlRight':
		case 'AltLeft':
		case 'AltRight':
		case 'ShiftLeft':
		case 'ShiftRight':
		case 'OSLeft':
		case 'OSRight':
		case 'Space':
		case 'CapsLock':
		case 'Fn':
			break;
		default:
			setSearchSuggesterVisibility(true);
			return;
	}
}

function navigateResults(payload: number) {
	if (payload === 1) {
		move('down');
	} else if (payload === -1) {
		move('up');
	}
}

async function move(direction: 'up' | 'down') {
	const items = searchResultsItems.value;
	const itemsLength = items.length ?? 0;

	if (direction === 'down') {
		if (index.value + 1 >= itemsLength) {
			index.value = 0;
		} else {
			index.value++;
		}
	} else if (direction === 'up') {
		index.value--;
		if (index.value < 0) {
			searchbar.value?.setFocus();
		}
	}

	if (items) items[index.value]?.$refs['resultItem']?.focus?.();
}

const searchResultsItems = computed(() => {
	// We need this computed property to listen to inputQuery.value changes so the new searchResults chidren $refs are accessed when the DOM updates
	if (!inputQuery.value) return;

	const titleDetailLinks =
		searchResults.value?.$children?.[0]?.$children.filter((child: { $vnode: { tag: string | string[] } }) => {
			return child.$vnode.tag.includes('TitleDetailLink');
		}) ?? [];

	return titleDetailLinks.map((link: { $children: any[] }) => link.$children?.[0]);
});

function clear(source: string) {
	TrackingHelper.trackEvent('search_suggest_clear', {
		action: source,
	});

	// TITLE_DETAIL_REDESIGN_NEW_BUYBOX
	// trackTitleDetailRedesignNewBuyBoxExp({ action: 'click', label: 'search_suggest_clear' });
	// TITLE_DETAIL_REDESIGN_NEW_BUYBOX
}

function navigateToSearchPage(searchQuery: string) {
	setSearchSuggesterVisibility(false);
	goToSearch({ searchString: searchQuery });
	TrackingHelper.trackEvent('search_suggest_more', {
		action: 'enter',
		label: searchQuery,
	});

	// TITLE_DETAIL_REDESIGN_NEW_BUYBOX
	// trackTitleDetailRedesignNewBuyBoxExp({ action: 'click', label: 'search_suggest_more' });
	// TITLE_DETAIL_REDESIGN_NEW_BUYBOX
}

return { __sfc: true,SearchSuggesterResults, props, emits, debounceValue, isMounted, inputQuery, searchbar, searchResults, index, t, activeRoute, isSearchSuggesterOpen, query, search, goToSearch, setSearchSuggesterVisibility, viewport, placeholder, showCancelButtonMobileWebApp, showCancelButton, openSearchBar, closeSearchBar, onChange, editSearch, navigateElements, navigateResults, move, searchResultsItems, clear, navigateToSearchPage }
}

})